import React, { memo, useMemo, useCallback, useImperativeHandle } from 'react';
import { CourseBuilderBlockContext, useCourseBuilderContext } from '../../CourseBuilderContext';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { BlockBase, BlockBaseHandle } from '@modules/course/CourseBuilder/Blocks/BlockBase/BlockBase';
import { PageBottomToolbar } from './PageBottomToolbar';
import { CourseBuilderBlockFormData } from '../../CourseBuilderForm';
import { CourseBuilder } from '@modules/course/CourseBuilder/Blocks/types';
import { BlockBaseReadonly } from '@modules/course/CourseBuilder/Blocks/BlockBase/BlockBaseReadonly';
import { useLazyEffect } from '@core/utils/react';
import { UserCourseStepResponse } from '@business/entities/user_course';
import { openCopyBlockTemplateModal } from './CopyBlockTemplateModal';
import { R } from '@core/utils/r';
import './index.scss';

export interface CourseBuilderBlocsHandle {
  openSettings(index: number): void;
}
export interface CourseBuilderBlocsProps {
  blockRefs: React.RefObject<HTMLDivElement>[];
  moveTo(index: number): void;
  editable?: boolean;
  handlesRef?: React.RefObject<CourseBuilderBlocsHandle>;
  lessonId?: string;
  step?: UserCourseStepResponse;
}
export const CourseBuilderBlocs = memo((props: CourseBuilderBlocsProps) => {
  const { blockRefs, moveTo, lessonId, step, editable = true } = props;
  const { form } = useCourseBuilderContext()!;
  const formValue = useNonNilObservable(form.value$);
  const blocks = useMemo(() => R.sortBy(formValue.blocks, b => (b?.menuOrder || 0).toString()), [formValue.blocks]);
  const blockItems = useObservable(form.controls.blocks.items$, []);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const handleRefs = useMemo(() => blockItems.map(() => React.createRef<BlockBaseHandle>()), [blockItems]);
  const [openSettingsIndex, setOpenSettingsIndex] = React.useState<number | null>(null);
  const openSettings = useCallback(
    (blockIndex: number) => {
      if (handleRefs[blockIndex] && handleRefs[blockIndex].current) {
        handleRefs[blockIndex].current!.openSettings();
      }
    },
    [handleRefs],
  );
  useLazyEffect(() => {
    if (openSettingsIndex !== null) {
      openSettings(openSettingsIndex);
      setOpenSettingsIndex(null);
    }
  }, [openSettingsIndex, openSettings]);
  useImperativeHandle(props.handlesRef, () => ({ openSettings }));

  const onDelete = useCallback(
    (id: string) => {
      const newBlock = R.pipe(
        formValue.blocks,
        R.ops.filter(b => b.id !== id),
        R.ops.sortBy(b => b.menuOrder),
      ).map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlock });
    },
    [form, formValue.blocks],
  );

  const addNewBlock = useCallback(
    (type: CourseBuilder.BlockType, variation: string, at?: number, data?: CourseBuilderBlockFormData) => {
      const menuOrder = at || blockItems.length;
      const newBlock: CourseBuilderBlockFormData = {
        id: `widget-${type}-${variation}` + R.random(1, 9999),
        type,
        variation,
        menuOrder,
        data: (data && data.data) || null,
        isSection: data ? data.isSection : false,
        title: undefined,
      };
      let newBlocks = [...form.value.blocks].map(b => {
        return {
          ...b,
          menuOrder: b.menuOrder >= menuOrder ? b.menuOrder + 1 : b.menuOrder,
        };
      });
      newBlocks.push(newBlock);
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
      moveTo(at || newBlocks.length - 1);
      if (
        [
          CourseBuilder.BlockType.orgChart,
          CourseBuilder.BlockType.jobDescription,
          CourseBuilder.BlockType.flowChart,
          CourseBuilder.BlockType.policy,
          CourseBuilder.BlockType.procedure,
          CourseBuilder.BlockType.glossary,
        ].includes(type)
      )
        setOpenSettingsIndex(at || newBlocks.length - 1);
    },
    [blockItems.length, form, moveTo],
  );
  const onAdd = useCallback(
    (type: CourseBuilder.BlockType, variation: string, at?: number, data?: CourseBuilderBlockFormData[]) => {
      if (type === CourseBuilder.BlockType.blockTemplate) {
        if (!!data?.length) {
          R.reverse(data).forEach(d => {
            addNewBlock(d.type, d.variation, at, d);
          });
        }
      } else {
        addNewBlock(type, variation, at);
      }
    },
    [addNewBlock],
  );
  const onMove = useCallback(
    (id: string, direction: number) => {
      const blockIndex = formValue.blocks.findIndex(b => b.id === id);
      const blockA = formValue.blocks[blockIndex];
      const blockB = formValue.blocks[blockIndex + direction];
      const newBlockA = { ...blockA, menuOrder: blockA.menuOrder + direction };
      const newBlockB = { ...blockB, menuOrder: blockB.menuOrder - direction };
      let newBlocks = formValue.blocks.map(b => {
        if (b.id === id) return newBlockA;
        if (b.id === blockB.id) return newBlockB;
        return b;
      });
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
    },
    [form, formValue.blocks],
  );
  const onCopyBlock = useCallback(
    (id: string) => {
      const blocks = formValue.blocks;
      openCopyBlockTemplateModal({ id, blocks, blockItems: blockItems });
    },
    [blockItems, formValue.blocks],
  );
  const onCopy = useCallback(
    (id: string) => {
      const newBlock = { ...formValue.blocks.find(b => b.id === id)! };
      newBlock.id = newBlock.id + R.random(1, 1000);
      newBlock.menuOrder += 1;
      let newBlocks = formValue.blocks.map(b => {
        return {
          ...b,
          menuOrder: b.menuOrder >= newBlock.menuOrder ? b.menuOrder + 1 : b.menuOrder,
        };
      });
      newBlocks.push(newBlock);
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
    },
    [form, formValue.blocks],
  );
  const blocksContent = useMemo(() => {
    return blockItems.map((blockForm, index) => {
      // Workaround to avoid error when block is deleted or page is moved
      if (!blockForm) return <div key={R.random(1, 9999).toString()}></div>;
      // console.log('blockForm.value.id', blockForm.value.id);
      if (!blockForm.value.type) return <div key={blockForm.value.id.toString()}></div>;
      // if (blockForm.value.id !== block.id) return <div key={block.id.toString()}></div>;
      // <div key={R.random(1, 9999).toString()}>
      return (
        <div key={editable ? blockForm.value.id : R.random(1, 9999).toString()}>
          <CourseBuilderBlockContext.Provider value={{ blockForm, blockIndex: index, step }}>
            {editable && (
              <BlockBase
                ref={blockRefs[index]}
                isLast={index === blockItems.length - 1}
                isFirst={index === 0}
                onAdd={onAdd}
                onDelete={onDelete}
                onMove={onMove}
                onCopy={onCopy}
                handleRef={handleRefs[index]}
                lessonId={lessonId}
                onCopyBlock={onCopyBlock}
              />
            )}
            {!editable && <BlockBaseReadonly lessonId={lessonId} />}
          </CourseBuilderBlockContext.Provider>
        </div>
      );
    });
  }, [blockItems, step, editable, blockRefs, onAdd, onDelete, onMove, onCopy, handleRefs, lessonId, onCopyBlock]);
  return (
    <div className="course-builder-page builder">
      <div ref={contentRef}>{blocksContent}</div>
      {editable && (
        <div style={{ overflow: 'auto' }}>
          <PageBottomToolbar onAdd={onAdd} size={blocks.length} />
        </div>
      )}
    </div>
  );
});
